import { useState } from 'react';
import type { BlogPostAdminListItem } from '@bailafm/shared';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { ApiClientError } from '@/lib/api';

interface BlogDeleteDialogProps {
  post: BlogPostAdminListItem | null;
  onClose(): void;
  onConfirm(post: BlogPostAdminListItem): Promise<void>;
}

export function BlogDeleteDialog({ post, onClose, onConfirm }: BlogDeleteDialogProps) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    if (!post) return;
    setPending(true);
    setError(null);
    try {
      await onConfirm(post);
      toast.success('Entrada eliminada');
      onClose();
    } catch (err) {
      const message =
        err instanceof ApiClientError && err.code === 'etag_mismatch'
          ? 'Otro usuario modificó esta entrada. Recarga la lista y vuelve a intentarlo.'
          : err instanceof ApiClientError
            ? err.message
            : 'No se pudo eliminar la entrada.';
      setError(message);
      toast.error(message);
    } finally {
      setPending(false);
    }
  }

  const preview = post ? (post.title.es ?? post.title.ca ?? post.title.en ?? post.id) : '';

  return (
    <AlertDialog
      open={Boolean(post)}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Eliminar entrada</AlertDialogTitle>
          <AlertDialogDescription>
            ¿Quieres eliminar la entrada <strong>{preview}</strong>? Esta acción no se puede
            deshacer.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              void handleConfirm();
            }}
            disabled={pending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {pending ? 'Eliminando…' : 'Eliminar'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
